import { markRaw } from 'vue'

// Import PaymentRequest column components
import PaymentRequestTimesheetPeriodColumn from '../Shared/PaymentRequestTimesheetPeriodColumn.vue'
import PaymentRequestPrIdColumn from '../Shared/PaymentRequestPrIdColumn.vue'
import PaymentRequestExpensesColumn from '../Shared/PaymentRequestExpensesColumn.vue'
import PaymentRequestStatusColumn from '../Shared/PaymentRequestStatusColumn.vue'
import PaymentRequestWorkerColumn from './PaymentRequestWorkerColumn.vue'
import PaymentRequestClientColumn from './PaymentRequestClientColumn.vue'
import PaymentRequestJobColumn from './PaymentRequestJobColumn.vue'
import PaymentRequestClientApprovalDateColumn from './PaymentRequestClientApprovalDateColumn.vue'
import PaymentRequestRateColumn from './PaymentRequestRateColumn.vue'
import PaymentRequestUnitsColumn from './PaymentRequestUnitsColumn.vue'
import PaymentRequestClientPaymentStatusColumn from './PaymentRequestClientPaymentStatusColumn.vue'
import PaymentRequestAssignmentCompanyColumn from './PaymentRequestAssignmentCompanyColumn.vue'
import PaymentRequestBatchColumn from './PaymentRequestBatchColumn.vue'
import PaymentRequestBatchCreatedAtColumn from './PaymentRequestBatchCreatedAtColumn.vue'
import PaymentRequestWorkLocationColumn from './PaymentRequestWorkLocationColumn.vue'
import PaymentRequestPayrolledAtColumn from './PaymentRequestPayrolledAtColumn.vue'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

// Import GraphQL fragments and types
import {
  PaymentRequestWorkerColumnFragmentDoc,
  PaymentRequestClientColumnFragmentDoc,
  PaymentRequestJobColumnFragmentDoc,
  PaymentRequestClientApprovalDateColumnFragmentDoc,
  PaymentRequestTimesheetPeriodColumnFragmentDoc,
  PaymentRequestPrIdColumnFragmentDoc,
  PaymentRequestRateColumnFragmentDoc,
  PaymentRequestUnitsColumnFragmentDoc,
  PaymentRequestExpensesColumnFragmentDoc,
  PaymentRequestStatusColumnFragmentDoc,
  PaymentRequestClientPaymentStatusColumnFragmentDoc,
  PaymentRequestAssignmentCompanyColumnFragmentDoc,
  PaymentRequestBatchColumnFragmentDoc,
  PaymentRequestBatchCreatedAtColumnFragmentDoc,
  PaymentRequestWorkLocationColumnFragmentDoc,
  PaymentRequestPayrolledAtColumnFragmentDoc,
} from '@/gql/graphql'

// Import fragment types for type safety
import type {
  PaymentRequestWorkerColumnFragment,
  PaymentRequestClientColumnFragment,
  PaymentRequestJobColumnFragment,
  PaymentRequestClientApprovalDateColumnFragment,
  PaymentRequestTimesheetPeriodColumnFragment,
  PaymentRequestPrIdColumnFragment,
  PaymentRequestRateColumnFragment,
  PaymentRequestUnitsColumnFragment,
  PaymentRequestExpensesColumnFragment,
  PaymentRequestStatusColumnFragment,
  PaymentRequestClientPaymentStatusColumnFragment,
  PaymentRequestAssignmentCompanyColumnFragment,
  PaymentRequestBatchColumnFragment,
  PaymentRequestBatchCreatedAtColumnFragment,
  PaymentRequestWorkLocationColumnFragment,
  PaymentRequestPayrolledAtColumnFragment,
} from '@/gql/graphql'

// PaymentRequest column definitions
const PartnerPaymentRequestColumns: ColumnMaps = {
  PaymentRequestWorkerColumn: {
    fragment: PaymentRequestWorkerColumnFragmentDoc,
    component: markRaw(PaymentRequestWorkerColumn),
    columnDef: {
      title: $t('Worker name and email'),
      key: 'worker',
      renderUsing: {
        component: markRaw(PaymentRequestWorkerColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Worker name',
        extraction: (_row: PaymentRequestWorkerColumnFragment) => _row.worker?.name || 'No name',
      },
      {
        title: 'Worker email',
        extraction: (_row: PaymentRequestWorkerColumnFragment) => _row.worker?.email || 'No email',
      },
    ],
  },
  PaymentRequestClientColumn: {
    fragment: PaymentRequestClientColumnFragmentDoc,
    component: markRaw(PaymentRequestClientColumn),
    columnDef: {
      title: $t('Client'),
      key: 'client',
      renderUsing: {
        component: markRaw(PaymentRequestClientColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Client',
        extraction: (row: PaymentRequestClientColumnFragment) =>
          row.hire?.company?.name || 'No client',
      },
    ],
  },
  PaymentRequestJobColumn: {
    fragment: PaymentRequestJobColumnFragmentDoc,
    component: markRaw(PaymentRequestJobColumn),
    columnDef: {
      title: $t('Worksome Hire title and ID'),
      key: 'job',
      renderUsing: {
        component: markRaw(PaymentRequestJobColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Worksome Hire title',
        extraction: (row: PaymentRequestJobColumnFragment) =>
          row.hire?.activeContract?.jobName || 'No title',
      },
    ],
  },
  PaymentRequestWorkLocationColumn: {
    fragment: PaymentRequestWorkLocationColumnFragmentDoc,
    component: markRaw(PaymentRequestWorkLocationColumn),
    columnDef: {
      title: $t('Work location'),
      key: 'work_location',
      renderUsing: {
        component: markRaw(PaymentRequestWorkLocationColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Work location',
        extraction: (row: PaymentRequestWorkLocationColumnFragment) =>
          row.hire?.activeContract?.location?.country?.name || 'No location',
      },
    ],
  },
  PaymentRequestClientApprovalDateColumn: {
    fragment: PaymentRequestClientApprovalDateColumnFragmentDoc,
    component: markRaw(PaymentRequestClientApprovalDateColumn),
    columnDef: {
      title: $t('Client approval date'),
      key: 'client_approval',
      renderUsing: {
        component: markRaw(PaymentRequestClientApprovalDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Client approval date',
        extraction: (row: PaymentRequestClientApprovalDateColumnFragment) =>
          row.manuallyApprovedAt || 'Not approved',
      },
    ],
  },
  PaymentRequestTimesheetPeriodColumn: {
    fragment: PaymentRequestTimesheetPeriodColumnFragmentDoc,
    component: markRaw(PaymentRequestTimesheetPeriodColumn),
    columnDef: {
      title: $t('Timesheet period'),
      key: 'period',
      renderUsing: {
        component: markRaw(PaymentRequestTimesheetPeriodColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Start date',
        extraction: (row: PaymentRequestTimesheetPeriodColumnFragment) =>
          row.startDate || 'No start date',
      },
      {
        title: 'End date',
        extraction: (row: PaymentRequestTimesheetPeriodColumnFragment) =>
          row.endDate || 'No end date',
      },
    ],
  },
  PaymentRequestPrIdColumn: {
    fragment: PaymentRequestPrIdColumnFragmentDoc,
    component: markRaw(PaymentRequestPrIdColumn),
    columnDef: {
      title: $t('PR ID'),
      key: 'pr_id',
      renderUsing: {
        component: markRaw(PaymentRequestPrIdColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Payment request number',
        extraction: (row: PaymentRequestPrIdColumnFragment) => row.number || 'No number',
      },
    ],
  },
  PaymentRequestRateColumn: {
    fragment: PaymentRequestRateColumnFragmentDoc,
    component: markRaw(PaymentRequestRateColumn),
    columnDef: {
      title: $t('Pay rate'),
      key: 'rate',
      renderUsing: {
        component: markRaw(PaymentRequestRateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Rate',
        extraction: (row: PaymentRequestRateColumnFragment) => `${row.currency} ${row.rate}`,
      },
      {
        title: 'Rate type',
        extraction: (row: PaymentRequestRateColumnFragment) => row.rateTypeName || 'No rate type',
      },
    ],
  },
  PaymentRequestUnitsColumn: {
    fragment: PaymentRequestUnitsColumnFragmentDoc,
    component: markRaw(PaymentRequestUnitsColumn),
    columnDef: {
      title: $t('Units'),
      key: 'units',
      renderUsing: {
        component: markRaw(PaymentRequestUnitsColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Units',
        extraction: (row: PaymentRequestUnitsColumnFragment) => row.rateQuantity?.toString() || '0',
      },
    ],
  },
  PaymentRequestExpensesColumn: {
    fragment: PaymentRequestExpensesColumnFragmentDoc,
    component: markRaw(PaymentRequestExpensesColumn),
    columnDef: {
      title: $t('Expenses'),
      key: 'expenses',
      renderUsing: {
        component: markRaw(PaymentRequestExpensesColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Expenses amount',
        extraction: (row: PaymentRequestExpensesColumnFragment) =>
          row.expensesAmount ? `${row.currency} ${row.expensesAmount}` : 'No expenses',
      },
    ],
  },
  PaymentRequestStatusColumn: {
    fragment: PaymentRequestStatusColumnFragmentDoc,
    component: markRaw(PaymentRequestStatusColumn),
    columnDef: {
      title: $t('Status'),
      key: 'status',
      renderUsing: {
        component: markRaw(PaymentRequestStatusColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Payment status',
        extraction: (row: PaymentRequestStatusColumnFragment) => (row.paidAt ? 'Paid' : 'Not paid'),
      },
    ],
  },
  PaymentRequestClientPaymentStatusColumn: {
    fragment: PaymentRequestClientPaymentStatusColumnFragmentDoc,
    component: markRaw(PaymentRequestClientPaymentStatusColumn),
    columnDef: {
      title: $t('Client payment status'),
      key: 'client_payment',
      renderUsing: {
        component: markRaw(PaymentRequestClientPaymentStatusColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Client payment status',
        extraction: (row: PaymentRequestClientPaymentStatusColumnFragment) =>
          row.paidAt ? 'Paid' : 'Not paid',
      },
    ],
  },
  PaymentRequestAssignmentCompanyColumn: {
    fragment: PaymentRequestAssignmentCompanyColumnFragmentDoc,
    component: markRaw(PaymentRequestAssignmentCompanyColumn),
    columnDef: {
      title: $t('Assignment company'),
      key: 'assignment_company',
      renderUsing: {
        component: markRaw(PaymentRequestAssignmentCompanyColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Assignment company',
        extraction: (row: PaymentRequestAssignmentCompanyColumnFragment) =>
          row.hire?.company?.name || row.company?.name || 'No assignment company',
      },
    ],
  },
  PaymentRequestBatchColumn: {
    fragment: PaymentRequestBatchColumnFragmentDoc,
    component: markRaw(PaymentRequestBatchColumn),
    columnDef: {
      title: $t('Batch ID'),
      key: 'batch_id',
      renderUsing: {
        component: markRaw(PaymentRequestBatchColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Batch names',
        extraction: (row: PaymentRequestBatchColumnFragment) => {
          const batches = row.batches?.data || []
          return batches.map((batch) => batch.name).join(', ') || 'No batch'
        },
      },
    ],
  },
  PaymentRequestBatchCreatedAtColumn: {
    fragment: PaymentRequestBatchCreatedAtColumnFragmentDoc,
    component: markRaw(PaymentRequestBatchCreatedAtColumn),
    columnDef: {
      title: $t('Processed'),
      key: 'processed',
      renderUsing: {
        component: markRaw(PaymentRequestBatchCreatedAtColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Processed',
        extraction: (row: PaymentRequestBatchCreatedAtColumnFragment) => {
          const batches = row.batches?.data || []
          return batches.map((batch) => batch.createdAt).join(', ') || 'No batch'
        },
      },
    ],
  },
  PaymentRequestPayrolledAtColumn: {
    fragment: PaymentRequestPayrolledAtColumnFragmentDoc,
    component: markRaw(PaymentRequestPayrolledAtColumn),
    columnDef: {
      title: $t('Completed'),
      key: 'completed',
      renderUsing: {
        component: markRaw(PaymentRequestPayrolledAtColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Completed',
        extraction: (row: PaymentRequestPayrolledAtColumnFragment) => row.payrolledAt || '--',
      },
    ],
  },
}

// Export the column definitions
export default PartnerPaymentRequestColumns
